/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2024 (original work) Open Assessment Technologies SA
 *
 */

import request, { getEndpointUrl, jwtTokenHandler } from '@/core/apiRequest/apiRequest';
import { parseJwtPayload } from 'core/jwt/jwtToken';
import config from '@/config';
import baseRequest from 'core/fetchRequest';
import _ from 'lodash';
import { getSubdomain } from '../helpers/domainHelper';
import router from '@/core/router';
import { user as authStore } from '@/stores/auth.store.js';
import { ROLE_TEST_TAKER } from '@/constants/roles.js';
import * as sessionStorageHelper from '@/helpers/sessionStorageHelper.js';
import * as analyticsService from '@/services/analyticsService';
import { logActivity } from './activityLogService';

/**
 * @typedef User
 * @property {string[]} roles
 */

/**
 * Returns the tenant ID from the stored access token
 * @async
 * @returns {string|null}
 */
export const getTenantId = async () => {
    const token = await jwtTokenHandler.getToken();
    return parseJwtPayload(token).tenant_id || null;
};

const getGroupRoles = async () => {
    const url = getEndpointUrl('roles', {}, {});
    return request(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json'
        }
    });
};

/**
 * Returns authorized user
 * @returns {Promise<User|null>}
 */
export const getUser = async () => {
    let token = await jwtTokenHandler.getToken();
    const jwtObject = parseJwtPayload(token) || {};
    const { hierarchy } = jwtObject;
    let roles = await getRoles(jwtObject.roles);
    return {
        userId: jwtObject.sub,
        roles,
        hierarchy
    };
};

async function getRoles(jwtRoles) {
    const userGroupRoles = await getGroupRoles();
    let roles = (jwtRoles || []).concat(userGroupRoles);
    roles = _.uniq(roles.filter(role => role !== ''));
    const preferredRole = getPreferredRole(); // See README to understand preferred roles
    if (roles.length > 1 && preferredRole) {
        if (preferredRole === ROLE_TEST_TAKER) {
            roles = roles.filter(role => role === ROLE_TEST_TAKER); // leave only test taker role
        } else {
            roles = roles.filter(role => role !== ROLE_TEST_TAKER); // remove test taker role
        }
    }
    return roles;
}

/**
 * Updates access tokens in store
 * @param {{accessToken: string, refreshToken: string}} tokens
 * @returns {[]}
 */
export const storeTokens = async ({ accessToken, refreshToken }) =>
    await Promise.all([jwtTokenHandler.storeRefreshToken(refreshToken), jwtTokenHandler.storeAccessToken(accessToken)]);

/**
 * Sign in proctoring and saves access tokens
 * @param {{username: string, password: string, clientId: string}} credentials
 * @returns {void}
 */
export const login = async ({ username, password, clientId }) => {
    const formData = new FormData();

    formData.append('grant_type', 'password');
    formData.append('client_id', clientId);
    formData.append('username', username.trim());
    formData.append('password', password);

    const response = await baseRequest(getEndpointUrl('login', {}, {}, config.baseUrls.authServer), {
        method: 'POST',
        body: formData
    });

    const { access_token: accessToken, refresh_token: refreshToken } = response;

    storeTokens({ accessToken, refreshToken });
};

/**
 * Redirect to Auth Server's SSO-Login URL, which will handle remaining SSO Auth flow
 * @param {{clientId: string, providerId: string}} params
 */
export const ssoLogin = async params => {
    const authServerSSOLoginUrl = getEndpointUrl('ssoLogin', params, {}, config.baseUrls.authServer);

    await router.stopActiveController();
    router.navigateOut(authServerSSOLoginUrl);
};

/**
 * @typedef TenantData
 * @property {string} tenantId
 * @property {string} portalClientId
 * @property {string} msClientId
/**
 * Gets an array of tenant metadata from Tenant API
 * @returns {TenantData}
 */
export const getTenant = async () => {
    const subdomain = getSubdomain();
    const type = 'portal';
    return await baseRequest(getEndpointUrl('subdomain', { type, subdomain }, {}, config.baseUrls.authServer), {});
};

/**
 * Checks whether the user has multiple roles. In that case it will ask to choose one. See `Preferred roles` in the REAMDE
 * @returns {Boolean} True if need the user to choose a role
 */
export async function needsToChoosePreferredRole() {
    let roles = authStore.get()?.roles ?? [];
    if (roles.length > 1 && roles.includes(ROLE_TEST_TAKER)) {
        let preferredRole = getPreferredRole();
        return !preferredRole;
    }
    return false;
}

const PREFERRED_ROLE_KEY = 'preferredRoleKey';

export function setPreferredRole(preferredRole) {
    return sessionStorageHelper.setItem(PREFERRED_ROLE_KEY, preferredRole);
}

function getPreferredRole() {
    return sessionStorageHelper.getItem(PREFERRED_ROLE_KEY);
}

function clearPreferredRole() {
    sessionStorageHelper.removeItem(PREFERRED_ROLE_KEY);
}
/**
 * Logs the user out and redirects to the login screen.
 * It also removes any session-related user information and analytics objects.
 */
export async function logout() {
    await logActivity({ action: 'logout' });
    await jwtTokenHandler.clearStore();
    clearPreferredRole();
    authStore.set(null);
    analyticsService.deactivate();
    router.redirect(config.routes.login.main);
}
